// WebSocket-relayed voice chat — X&O.
//
// Per-game copy of the WSVoice transport (see games/ludo/wsVoice.ts for
// the full design essay). The ONLY thing that varies between copies is
// the browser-global key used to hand off the pre-warmed MediaStream
// from the Play-button click gesture into the `start()` call that fires
// later (when xo_match_start arrives). Every game MUST use its own key
// — e.g. Ludo:`__ludoMicStream`, Chess:`__chessMicStream`, X&O:
// `__xoMicStream` — otherwise a mismatch causes `start()` to miss the
// warm stream and re-request getUserMedia outside the gesture context,
// which Safari / mobile Chrome silently refuses or degrades.
//
// This was the X&O-specific bug before the rename: Page.tsx wrote the
// stream to `__xoMicStream` but imported WSVoice from `../ludo/wsVoice`
// which reads `__ludoMicStream`, so every match fell back to the
// gesture-less re-request path → permission silently denied → broken
// voice, or granted-but-delayed → 10-20 s lag with breakage.
//
// Transport identical to Ludo / Chess / Rummy:
//   • 8 kHz mono Int16 PCM, 20 ms frames (160 samples = 320 bytes)
//   • base64 over the existing game WS as `voice_chunk`
//   • Server (games/xo/room.js → relayVoiceChunk) echoes each frame to
//     every other human member in the room.
//   • No WebRTC, no ICE/STUN/TURN, no peer connections. Latency tracks
//     the WS itself (~30–100 ms).

type Color = string;

const WIRE_SAMPLE_RATE = 8000;
const FRAME_SAMPLES    = 160;

type SendFn = (msg: { type: 'voice_chunk'; pcm: string }) => void;

export class WSVoice {
  private ownColor: Color;
  private send: SendFn;

  private micStream: MediaStream | null = null;
  private captureCtx: AudioContext | null = null;
  private captureNode: ScriptProcessorNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private sampleQueue: Float32Array[] = [];
  private sampleQueueLen = 0;
  private browserSampleRate = 48000;

  private playbackCtx: AudioContext | null = null;
  private nextStartTime = new Map<Color, number>();

  private muted = false;
  private listenMuted = false;
  private sentFrames = 0;
  private recvFrames = 0;

  constructor(ownColor: Color, send: SendFn) {
    this.ownColor = ownColor;
    this.send = send;
  }

  static async prewarmMic(): Promise<MediaStream | null> {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      (window as any).__xoMicStream = s;
      return s;
    } catch (e) {
      console.warn('[xo/voice] prewarmMic denied', e);
      return null;
    }
  }

  async start(): Promise<boolean> {
    try {
      const warm = (window as any).__xoMicStream as MediaStream | undefined;
      if (warm && warm.getAudioTracks().some((t) => t.readyState === 'live')) {
        this.micStream = warm;
        (window as any).__xoMicStream = null;
      } else {
        this.micStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      }
    } catch (e) {
      console.warn('[xo/voice] mic denied', e);
      return false;
    }

    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    this.captureCtx = new Ctx();
    this.browserSampleRate = this.captureCtx.sampleRate;
    this.sourceNode = this.captureCtx.createMediaStreamSource(this.micStream);
    this.captureNode = this.captureCtx.createScriptProcessor(2048, 1, 1);
    this.sourceNode.connect(this.captureNode);
    // Must sink into destination (at gain 0) or some browsers stop firing
    // the onaudioprocess callback. Zero-gain means no self-echo.
    const silent = this.captureCtx.createGain();
    silent.gain.value = 0;
    this.captureNode.connect(silent);
    silent.connect(this.captureCtx.destination);

    this.captureNode.onaudioprocess = (ev) => {
      if (this.muted) return;
      const input = ev.inputBuffer.getChannelData(0);
      const copy = new Float32Array(input.length);
      copy.set(input);
      this.sampleQueue.push(copy);
      this.sampleQueueLen += copy.length;
      this.drainToFrames();
    };

    this.playbackCtx = new Ctx();
    if (this.playbackCtx.state === 'suspended') {
      try { await this.playbackCtx.resume(); } catch {}
    }
    console.info('[xo/voice] started, browserRate=', this.browserSampleRate, 'wire=', WIRE_SAMPLE_RATE);
    return true;
  }

  private drainToFrames() {
    const ratio = this.browserSampleRate / WIRE_SAMPLE_RATE;
    const rawNeeded = Math.ceil(FRAME_SAMPLES * ratio);
    while (this.sampleQueueLen >= rawNeeded) {
      const raw = new Float32Array(rawNeeded);
      let filled = 0;
      while (filled < rawNeeded && this.sampleQueue.length) {
        const head = this.sampleQueue[0];
        const take = Math.min(rawNeeded - filled, head.length);
        raw.set(head.subarray(0, take), filled);
        filled += take;
        if (take === head.length) this.sampleQueue.shift();
        else this.sampleQueue[0] = head.subarray(take);
      }
      this.sampleQueueLen -= rawNeeded;

      const frame = new Int16Array(FRAME_SAMPLES);
      for (let i = 0; i < FRAME_SAMPLES; i++) {
        const src = raw[Math.floor(i * ratio)] || 0;
        const clamped = Math.max(-1, Math.min(1, src));
        frame[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
      }

      const b64 = int16ToBase64(frame);
      try { this.send({ type: 'voice_chunk', pcm: b64 }); } catch {}
      this.sentFrames++;
      if (this.sentFrames % 100 === 0) {
        console.info(`[xo/voice] sent ${this.sentFrames} frames from mark=${this.ownColor}`);
      }
    }
  }

  onRemoteChunk(from: Color, b64: string) {
    if (from === this.ownColor) return;
    if (this.listenMuted) return;
    if (!this.playbackCtx) {
      if (this.recvFrames++ % 100 === 0) console.warn('[xo/voice] received frame but no playbackCtx yet');
      return;
    }
    const frame = base64ToInt16(b64);
    if (!frame || frame.length === 0) return;
    this.recvFrames++;
    if (this.recvFrames % 100 === 0) {
      console.info(`[xo/voice] received ${this.recvFrames} frames from=${from}`);
    }
    const f32 = new Float32Array(frame.length);
    for (let i = 0; i < frame.length; i++) f32[i] = frame[i] / 0x8000;
    const buf = this.playbackCtx.createBuffer(1, frame.length, WIRE_SAMPLE_RATE);
    buf.copyToChannel(f32, 0);
    const src = this.playbackCtx.createBufferSource();
    src.buffer = buf;
    src.connect(this.playbackCtx.destination);

    const now = this.playbackCtx.currentTime;
    let at = this.nextStartTime.get(from) ?? now + 0.05;
    if (at < now) at = now + 0.02;
    src.start(at);
    this.nextStartTime.set(from, at + buf.duration);
  }

  setMuted(m: boolean) { this.muted = m; }
  isMuted() { return this.muted; }
  setListenMuted(m: boolean) { this.listenMuted = m; }
  isListenMuted() { return this.listenMuted; }

  hasLocalStream() {
    return !!this.micStream && this.micStream.getAudioTracks().some((t) => t.readyState === 'live');
  }

  // API-compat stubs (Ludo used to have a WebRTC mesh; those events still
  // ship from the server on seat-join so we swallow them quietly).
  onPeers(_peers: Color[]) { void _peers; }
  onSignal(_from: Color, _payload: any) { void _from; void _payload; }
  dropPeer(_color: Color) { void _color; }

  async attachLocalStream(stream: MediaStream) {
    (window as any).__xoMicStream = stream;
    await this.start();
  }

  kickAudioPlayback() {
    if (this.playbackCtx && this.playbackCtx.state === 'suspended') {
      this.playbackCtx.resume().catch(() => {});
    }
  }

  stop() {
    try { this.captureNode?.disconnect(); } catch {}
    try { this.sourceNode?.disconnect(); } catch {}
    try { this.captureCtx?.close(); } catch {}
    try { this.playbackCtx?.close(); } catch {}
    this.captureCtx = null;
    this.playbackCtx = null;
    this.captureNode = null;
    this.sourceNode = null;
    this.micStream?.getTracks().forEach((t) => t.stop());
    this.micStream = null;
    this.sampleQueue = [];
    this.sampleQueueLen = 0;
    this.nextStartTime.clear();
  }
}

// ─── tiny base64 helpers for Int16 PCM frames ────────────────────────────

function int16ToBase64(arr: Int16Array): string {
  const bytes = new Uint8Array(arr.buffer, arr.byteOffset, arr.byteLength);
  let s = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    s += String.fromCharCode.apply(
      null,
      bytes.subarray(i, i + CHUNK) as unknown as number[],
    );
  }
  return btoa(s);
}

function base64ToInt16(b64: string): Int16Array | null {
  try {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    if (bytes.length % 2 !== 0) return null;
    return new Int16Array(bytes.buffer, bytes.byteOffset, bytes.length / 2);
  } catch {
    return null;
  }
}
